import { DIAMONDS, RANK_THRESHOLDS, VAULTS } from "../data/balance";
import {
  CAMPAIGN_LEVELS,
  campaignLevel,
  difficultyTagFor,
  endlessDifficulty,
  masterRuleName,
  rewardUnlockFor,
  securityBriefFor,
  starsForTime,
  type CampaignLevel,
} from "../data/campaignLevels";
import { SaveRepository } from "../data/SaveRepository";
import { Vector2 } from "../core/math/Vector2";
import { GameClock } from "../core/time/GameClock";
import { CollisionSystem, SpawnSystem } from "../domain/diamondSystems";
import { HazardSystem } from "../domain/HazardSystem";
import { MovementSystem } from "../domain/movement";
import type { ArenaBounds, Diamond, GameMode, GameState, Hazard, Player, PlayerAvatar, Point, RunResult, SaveData, VaultId } from "../domain/models";
import { SpecialDiamondSystem } from "../domain/SpecialDiamondSystem";
import { alertStateFor, type AlertState } from "../domain/SurvivalDifficulty";
import {
  objectiveComplete,
  objectiveName,
  objectiveProgress,
  objectiveText,
  objectiveValue,
  type ObjectiveStats,
} from "../domain/LevelObjective";
import {
  applyChapterUpgrade,
  EMPTY_CHAPTER_UPGRADES,
  upgradeChoicesFor,
  type ChapterUpgradeId,
  type ChapterUpgradeState,
} from "../domain/ChapterUpgrades";
import { ComboSystem, scoreFor, TimerSystem, xpFor } from "../domain/systems";
import { CanvasRenderer, type FloatingText, type Particle } from "../presentation/CanvasRenderer";
import { AudioManager } from "../presentation/AudioManager";
import { InputController } from "../presentation/input/InputController";
import { ResultsScreen, runTransitionMessageFor } from "../presentation/screens/ResultsScreen";
import type { AdService } from "../services/AdService";
import type { AnalyticsService } from "../services/AnalyticsService";
import { RewardedRunState } from "../domain/RewardedRunState";
import { securityHeatFor, type SecurityHeatState } from "../domain/SecurityHeat";
import { RELICS, relicBonuses, relicForVault } from "../domain/Relics";
import {
  CHAPTER_INTROS,
  CURRENT_APP_VERSION,
  FEATURE_DISCOVERY,
  controlCopyFor,
  featureAlreadySeen,
  shouldShowOnboarding,
  shouldShowWhatsNew,
  type PlatformContext,
} from "../domain/Onboarding";

type TutorialStep = "movement" | "diamond" | "timer" | "combo" | "hazard" | "special" | "objective" | "complete";

interface DiamondRushQaHooks {
  completeTraining(): void;
  completeCurrentLevel(): void;
  state(): GameState;
  save(): SaveData;
}

declare global {
  interface Window {
    __diamondRushQa?: DiamondRushQaHooks;
  }
}

export class GameApp {
  private state: GameState = "MAIN_MENU";
  private mode: GameMode = "campaign";
  private currentLevel?: CampaignLevel;
  private levelSucceeded = false;
  private save: SaveData;
  private vault: VaultId = "street";
  private readonly repository = new SaveRepository(localStorage);
  private readonly renderer: CanvasRenderer;
  private readonly clock = new GameClock(60);
  private readonly input = new InputController();
  private readonly results: ResultsScreen;
  private readonly movement = new MovementSystem();
  private readonly spawner = new SpawnSystem();
  private readonly collisions = new CollisionSystem();
  private readonly hazardSystem = new HazardSystem();
  private readonly specialDiamondSystem = new SpecialDiamondSystem();
  private readonly audio: AudioManager;
  private timer = new TimerSystem(VAULTS.street.time);
  private combo = new ComboSystem();
  private player: Player = { position: new Vector2(), radius: 20, speed: 310 };
  private diamonds: Diamond[] = [];
  private hazards: Hazard[] = [];
  private particles: Particle[] = [];
  private floatingTexts: FloatingText[] = [];
  private score = 0;
  private collected = 0;
  private elapsed = 0;
  private nextHazardId = 1;
  private hitCooldown = 0;
  private shakeAmount = 0;
  private bonusTimeEarned = 0;
  private shieldCharges = 0;
  private hazardsFrozenFor = 0;
  private freezeCooldown = 0;
  private nextHazardSpawnIn = 0;
  private previousAlertLevel = 0;
  private runEnding = false;
  private paused = false;
  private hazardCausedTimeout = false;
  private endTransitionRemaining = 0;
  private pendingEndReason?: NonNullable<RunResult["endReason"]>;
  private lastResult?: RunResult;
  private readonly rewardedRunState = new RewardedRunState();
  private continueOfferPending = false;
  private bestCombo = 0;
  private rareDiamonds = 0;
  private chapterUpgrades: ChapterUpgradeState = { ...EMPTY_CHAPTER_UPGRADES };
  private activeChapter = 1;
  private shieldUses = 0;
  private freezeUses = 0;
  private positionHistory: Point[] = [];
  private positionSampleTimer = 0;
  private relicUnlockedThisRun?: string;
  private hazardHits = 0;
  private hazardsSpawned = 0;
  private hazardsDestroyed = 0;
  private maximumHeat = 0;
  private specialDiamondsCollected = 0;
  private tutorialActive = false;
  private tutorialStep: TutorialStep = "movement";
  private tutorialStepElapsed = 0;
  private tutorialMovementDetected = false;

  private readonly frame: HTMLElement;
  private readonly menu: HTMLElement;
  private readonly onboarding: HTMLElement;
  private readonly onboardingEntry: HTMLElement;
  private readonly profileScreen: HTMLElement;
  private readonly statisticsScreen: HTMLElement;
  private readonly settingsScreen: HTMLElement;
  private readonly manualScreen: HTMLElement;
  private readonly discoveryCard: HTMLElement;
  private readonly whatsNewCard: HTMLElement;
  private readonly campaignMap: HTMLElement;
  private readonly vaultScreen: HTMLElement;
  private readonly resultScreen: HTMLElement;
  private readonly hud: HTMLElement;
  private readonly scoreHud: HTMLElement;
  private readonly timeHud: HTMLElement;
  private readonly comboHud: HTMLElement;
  private readonly timeFill: HTMLElement;
  private readonly alertHud: HTMLElement;
  private readonly shieldHud: HTMLElement;
  private readonly threatFill: HTMLElement;
  private readonly endTransition: HTMLElement;
  private readonly pauseOverlay: HTMLElement;
  private readonly objectiveProgress: HTMLElement;
  private readonly objectiveHud: HTMLElement;
  private readonly statusHud: HTMLElement;
  private readonly difficultyHud: HTMLElement;
  private readonly heatHud: HTMLElement;
  private readonly heatFill: HTMLElement;
  private readonly upgradePanel: HTMLElement = document.createElement("section");
  // Falls back to the original hardcoded value; recomputed from the actual
  // rendered HUD panels once they exist (see recomputeHudSafeTop()) so
  // diamonds never spawn hidden behind the HUD overlay, on any viewport.
  private hudSafeTop = 110;

  constructor(
    private readonly root: HTMLElement,
    private readonly ads: AdService,
    private readonly openPrivacyChoices: () => void,
    private readonly analytics: AnalyticsService,
  ) {
    this.save = this.repository.load();
    this.audio = new AudioManager(this.save.settings.soundEnabled, import.meta.env.BASE_URL);
    this.audio.playMusicLoop();
    this.root.innerHTML = this.template();
    const canvas = this.require<HTMLCanvasElement>("game-canvas");
    this.renderer = new CanvasRenderer(canvas);
    this.results = new ResultsScreen(this.root);
    this.frame = this.require("game-frame");
    this.menu = this.require("main-menu");
    this.onboarding = this.require("onboarding");
    this.onboardingEntry = this.require("onboarding-entry");
    this.profileScreen = this.require("profile-screen");
    this.statisticsScreen = this.require("statistics-screen");
    this.settingsScreen = this.require("settings-screen");
    this.manualScreen = this.require("manual-screen");
    this.discoveryCard = this.require("discovery-card");
    this.whatsNewCard = this.require("whats-new-card");
    this.campaignMap = this.require("campaign-map");
    this.vaultScreen = this.require("vault-select");
    this.resultScreen = this.require("results");
    this.hud = this.require("hud");
    this.scoreHud = this.require("score-hud");
    this.timeHud = this.require("time-hud");
    this.comboHud = this.require("combo-hud");
    this.timeFill = this.require("time-fill");
    this.alertHud = this.require("alert-hud");
    this.shieldHud = this.require("shield-hud");
    this.threatFill = this.require("threat-fill");
    this.endTransition = this.require("run-end-transition");
    this.pauseOverlay = this.require("pause-overlay");
    this.objectiveProgress = this.require("objective-progress");
    this.objectiveHud = this.require("objective-hud");
    this.statusHud = this.require("status-hud");
    this.difficultyHud = this.require("difficulty-hud");
    this.heatHud = this.require("heat-hud");
    this.heatFill = this.require("heat-fill");
    this.bind(canvas);
    this.refreshProfile();
    if (this.save.profile.name) {
      this.save = this.repository.updateProfile(this.save, this.save.profile.name, this.save.profile.avatar);
    }
    this.installQaHooks();
    this.track("app_opened", { surface: "web" });
    this.track("session_started", { surface: "web" });
    this.show(this.initialScreen());
    requestAnimationFrame(this.loop);
  }

  private track(name: string, properties: Record<string, unknown> = {}): void {
    this.analytics.track(name, properties);
  }

  private handlePrivacyChoices = (): void => {
    this.track("privacy_choices_opened", { screen: this.state });
    this.openPrivacyChoices();
  };

  private installQaHooks(): void {
    if (new URLSearchParams(window.location.search).get("qa") !== "1") return;
    window.__diamondRushQa = {
      completeTraining: () => {
        if (!this.tutorialActive) this.startTutorialRun();
        this.completeTutorialRun();
      },
      completeCurrentLevel: () => {
        if (this.state !== "PLAYING" || this.mode !== "campaign" || !this.currentLevel) {
          throw new Error("No active campaign level to complete");
        }
        this.levelSucceeded = true;
        this.startEndTransition("target_complete");
      },
      state: () => this.state,
      save: () => this.repository.load(),
    };
  }

  private template(): string {
    return `
      <main class="app-shell">
        <section class="game-frame" id="game-frame" aria-label="Diamond Dash game">
          <canvas id="game-canvas" aria-label="Vault play field"></canvas>
          <section id="onboarding" class="screen identity-screen" hidden>
            <form class="identity-card" id="identity-form">
              <div class="brand-mark">Create local profile</div>
              <h2>Who is cracking the vault?</h2>
              <p>Your name, avatar, progress, and statistics stay on this device. No account or email required.</p>
              <label class="name-field">Player name
                <input id="player-name-input" maxlength="18" autocomplete="nickname" placeholder="Vault Runner" required>
              </label>
              <fieldset class="avatar-picker" id="avatar-picker">
                <legend>Choose an avatar</legend>
                ${this.avatarOptions("cracker")}
              </fieldset>
              <button class="button" type="submit">Enter Diamond Dash</button>
              <button class="button secondary" id="guest-button" type="button">Continue as Guest</button>
            </form>
          </section>
          <section id="onboarding-entry" class="screen identity-screen" hidden>
            <article class="identity-card onboarding-choice">
              <div class="brand-mark">Training Operation</div>
              <h2>Welcome to Diamond Dash</h2>
              <p>Collect diamonds. Beat security systems. Escape with the loot.</p>
              <div class="tutorial-preview" id="tutorial-control-preview"></div>
              <div class="menu-actions">
                <button class="button gold" id="start-training-button" type="button">Start Training</button>
                <button class="button secondary" id="skip-training-button" type="button">Skip Training</button>
              </div>
              <p class="tip">Training is optional. Complete it once to earn +100 Gold.</p>
            </article>
          </section>
          <section id="main-menu" class="screen">
            <div class="home-layout">
              <div class="home-hero">
                <div class="brand-mark">Midnight Arcade</div>
                <h1>Diamond<br><span>Rush</span></h1>
                <p class="lead">Build your vault career. Master adaptive security, collect relics, and push your Heat record.</p>
                <article class="player-card">
                  <button class="avatar-badge" id="home-avatar" type="button" aria-label="Open profile">VC</button>
                  <div><span>Welcome back</span><strong id="home-player-name">Vault Runner</strong><small id="home-player-title">Vault Cracker - Rank 1</small></div>
                  <div class="rank-progress"><span id="rank-progress-fill"></span></div>
                </article>
                <div class="profile-strip">
                  <div>Best<strong id="best-value">0</strong></div>
                  <div>Gold<strong id="gold-value">0</strong></div>
                  <div>Heat<strong id="heat-record-value">0</strong></div>
                  <div>Stars<strong id="stars-value">0/90</strong></div>
                  <div>Chapter<strong id="chapter-value">1/6</strong></div>
                </div>
              </div>
              <nav class="home-actions" aria-label="Game modes">
                <button class="home-action primary" id="play-button"><span>Continue Campaign</span><strong id="continue-level">Level 1</strong></button>
                <button class="home-action" id="campaign-map-button"><span>Campaign Map</span><strong>30 vault operations</strong></button>
                <button class="home-action" id="endless-button"><span>Endless Rush</span><strong>High-score mode</strong></button>
                <button class="home-action daily" id="daily-button" disabled><span>Daily Vault - Coming Soon</span><strong>Locked</strong></button>
                <button class="home-action" id="profile-button"><span>Relics</span><strong id="profile-summary">Trophy Case</strong></button>
                <button class="home-action" id="statistics-button"><span>Statistics</span><strong>Lifetime records</strong></button>
                <button class="home-action" id="settings-button"><span>Settings</span><strong>Audio, motion, privacy</strong></button>
                <button class="home-action" id="manual-button"><span>Operations Manual</span><strong>Controls and systems</strong></button>
              </nav>
              <div class="setting-row" aria-label="Game settings">
                <button class="setting-toggle" id="sound-toggle" type="button">Sound: on</button>
                <button class="setting-toggle" id="motion-toggle" type="button">Motion: full</button>
              </div>
              <p class="tip" id="menu-tip">Drag or use WASD / arrow keys. Collect quickly to hold your combo.</p>
              ${this.adPlacement("menu")}
              <nav class="legal-links" aria-label="Legal">
                <a href="./privacy.html">Privacy</a>
                <a href="./cookies.html">Cookies</a>
                <a href="./terms.html">Terms</a>
                <button id="privacy-choices" type="button">Privacy choices</button>
                <a href="/games" id="back-to-games">Back to games</a>
              </nav>
            </div>
          </section>
          <section id="profile-screen" class="screen panel-screen" hidden>
            <div class="panel-content">
              <header class="panel-heading"><div><div class="brand-mark">Trophy Case</div><h2>Relics</h2></div><button class="button secondary" data-home>Home</button></header>
              <div class="profile-editor">
                <div class="profile-avatar-large" id="profile-avatar-large">VC</div>
                <form id="profile-form">
                  <label class="name-field">Player name<input id="profile-name-input" maxlength="18" autocomplete="nickname"></label>
                  <fieldset class="avatar-picker"><legend>Avatar</legend>${this.avatarOptions("cracker", "profile-avatar")}</fieldset>
                  <button class="button" type="submit">Save profile</button>
                </form>
              </div>
              <p class="collection-count" id="relic-count">0 of 6 relics collected</p>
              <div class="relic-collection" id="relic-collection"></div>
            </div>
          </section>
          <section id="statistics-screen" class="screen panel-screen" hidden>
            <div class="panel-content">
              <header class="panel-heading"><div><div class="brand-mark">Career records</div><h2>Statistics</h2></div><button class="button secondary" data-home>Home</button></header>
              <div class="statistics-grid" id="statistics-grid"></div>
            </div>
          </section>
          <section id="settings-screen" class="screen panel-screen" hidden>
            <div class="panel-content settings-content">
              <header class="panel-heading"><div><div class="brand-mark">Preferences</div><h2>Settings</h2></div><button class="button secondary" data-home>Home</button></header>
              <div class="settings-card">
                <button class="setting-toggle" id="settings-sound-toggle" type="button">Sound: on</button>
                <button class="setting-toggle" id="settings-motion-toggle" type="button">Motion: full</button>
                <button class="setting-toggle" id="control-scheme-toggle" type="button">Control: Drag Anywhere</button>
                <button class="button secondary" id="settings-replay-tutorial" type="button">Replay tutorial</button>
                <button class="button secondary" id="settings-manual" type="button">Operations Manual</button>
                <button class="button secondary" id="settings-privacy" type="button">Privacy choices</button>
                <p>Profile, progress, scores, relics, onboarding, and settings stay on this device. Virtual joystick is planned; Drag Anywhere remains the current touch default.</p>
              </div>
            </div>
          </section>
          <section id="manual-screen" class="screen panel-screen" hidden>
            <div class="panel-content manual-content">
              <header class="panel-heading"><div><div class="brand-mark">Reference</div><h2>Operations Manual</h2></div><button class="button secondary" id="manual-back-to-pause" type="button" hidden>Back to game</button><button class="button secondary" data-home>Home</button></header>
              <div class="manual-grid" id="manual-grid"></div>
            </div>
          </section>
          <section id="campaign-map" class="screen campaign-screen" hidden>
            <div class="campaign-content">
              <div class="brand-mark">Campaign</div>
              <div class="campaign-heading">
                <div><h2>Vault map</h2><p>Clear each job, earn three stars, and defeat every Master Vault.</p></div>
                <button class="button secondary" id="campaign-back" type="button">Main menu</button>
              </div>
              <div class="campaign-map-grid" id="campaign-map-grid"></div>
              ${this.adPlacement("campaign")}
            </div>
          </section>
          <section id="vault-select" class="screen" hidden>
            <div class="menu-content">
              <div class="brand-mark">Choose the job</div>
              <h2>Vault select</h2>
              <p class="lead">Street Safes are forgiving. Bank Vaults start hotter and carry twice the security.</p>
              <div class="vault-grid">
                <button class="vault-card" data-vault="street">
                  <span class="vault-state">Ready</span>
                  <span class="vault-icon">&#9671;</span><strong>Street Safes</strong>
                  <small>15 seconds &middot; light security &middot; ideal for learning the rush</small>
                </button>
                <button class="vault-card bank" data-vault="bank" id="bank-vault">
                  <span class="vault-state" id="bank-state">Locked</span>
                  <span class="vault-icon">&#9670;</span><strong>Bank Vaults</strong>
                  <small id="bank-copy">14 seconds &middot; heavy security &middot; higher pressure</small>
                </button>
              </div>
              <button class="button secondary" id="back-button">Back</button>
            </div>
          </section>
          <section id="hud" class="hud" hidden>
            <div class="hud-top">
              <div class="hud-block"><span class="hud-label">Score</span><strong class="hud-value" id="score-hud">0</strong></div>
              <div class="combo-readout" id="combo-hud"><span class="hud-label">Combo</span><strong>x1</strong></div>
              <div class="hud-block right"><span class="hud-label">Time</span><strong class="hud-value" id="time-hud">15.0</strong></div>
              <button class="hud-pause-button" id="pause-button" type="button" aria-label="Pause">&#10074;&#10074;</button>
            </div>
            <div class="objective-hud" id="objective-hud">
              <div id="objective-copy"></div>
              <div class="objective-track"><div class="objective-progress" id="objective-progress"></div></div>
            </div>
            <div class="difficulty-hud" id="difficulty-hud">Easy Run</div>
            <div class="threat-hud" id="alert-hud">
              <span id="alert-label">Warmup</span>
              <span class="shield-state" id="shield-hud">Shield empty</span>
              <div class="threat-track"><div class="threat-fill" id="threat-fill"></div></div>
            </div>
            <div class="status-hud" id="status-hud"><span id="freeze-hud">Freeze inactive</span><span id="counterplay-hud">Shield and freeze diamonds appear as security rises</span></div>
            <div class="heat-hud" id="heat-hud">
              <span id="heat-label">Heat Green - Normal rewards</span>
              <div class="heat-track"><div class="heat-fill" id="heat-fill"></div></div>
            </div>
            <div class="time-track"><div class="time-fill" id="time-fill"></div></div>
            <div class="low-time-warning" id="low-time-warning">Security closing</div>
          </section>
          <section class="run-end-transition" id="run-end-transition" hidden aria-live="assertive">
            <strong id="run-end-title">Time up</strong>
            <span id="run-end-detail">The timer reached zero.</span>
            <div class="run-end-actions" id="run-end-continue-offer" hidden>
              <button class="button gold" id="run-end-continue-button" type="button">Watch ad: Continue run</button>
              <button class="button secondary" id="run-end-skip-button" type="button">End run</button>
            </div>
          </section>
          <section class="run-end-transition pause-overlay" id="pause-overlay" hidden aria-live="assertive">
            <strong>Paused</strong>
            <div class="run-end-actions">
              <button class="button gold" id="pause-resume-button" type="button">Resume</button>
              <button class="button secondary" id="pause-manual-button" type="button">How to play</button>
              <button class="button secondary" id="pause-menu-button" type="button">Main menu</button>
              <a class="button secondary" id="pause-back-to-games" href="/games">Back to games</a>
            </div>
          </section>
          <section id="results" class="screen" hidden>
            <article class="result-card">
            <div class="result-scroll">
              <div class="result-kicker" id="result-kicker">Vault closed</div>
              <h2 id="result-heading">Run complete</h2>
              <p class="result-reason" id="result-reason"></p>
              <section class="result-outcome" id="result-outcome" data-tone="neutral">
                <span id="result-outcome-label">Run status</span>
                <strong id="result-outcome-title">Run complete</strong>
                <p id="result-outcome-body">Review your result and choose what to do next.</p>
              </section>
              <div class="result-level" id="result-level"></div>
              <div class="result-objective" id="result-objective"></div>
              <div class="result-stars" id="result-stars"></div>
              <div class="result-relic" id="result-relic"></div>
              <div class="result-score-wrap">
                <span>Final Score</span>
                <div class="result-score" id="result-score">0</div>
              </div>
              <div class="result-primary-grid">
                <div><span>Score</span><strong id="result-primary-score">0</strong></div>
                <div><span>Stars</span><strong id="result-primary-stars">0</strong></div>
                <div><span>Gold earned</span><strong id="result-primary-gold">+0</strong></div>
                <div><span>XP earned</span><strong id="result-xp">+0</strong></div>
              </div>
              <button class="button gold double-gold-button" id="results-double-gold-button" type="button" hidden>Watch ad: Double gold</button>
              <button class="details-toggle" id="details-toggle" type="button">View details</button>
              <section class="result-details" id="result-details" hidden>
                <div class="result-grid">
                  <div><span>Diamonds</span><strong id="result-diamonds">0</strong></div>
                  <div><span>Best combo</span><strong id="result-combo">x0</strong></div>
                  <div><span>Heat reached</span><strong id="result-heat">0</strong></div>
                </div>
                <div class="record-badges" id="record-badges"></div>
              </section>
              <p class="so-close" id="so-close"></p>
            </div>
              <div class="menu-actions">
                <button class="button gold" id="next-level-button">Next level</button>
                <button class="button secondary" id="replay-button">Replay level</button>
                <button class="button secondary" id="results-map-button">Campaign Map</button>
                <button class="button secondary" id="results-leaderboard-button" type="button">Leaderboard</button>
                <button class="button secondary" id="share-result-button" type="button">Share score</button>
                <span class="share-icons">
                  <button class="button secondary icon-button" id="share-facebook" type="button" aria-label="Share on Facebook"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879v-6.988h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.891h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg></button>
                  <button class="button secondary icon-button" id="share-x" type="button" aria-label="Share on X"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></button>
                </span>
                <button class="button secondary" id="menu-button">Main menu</button>
                <a class="button secondary" href="/games" id="result-back-to-games">Back to games</a>
              </div>
            </article>
          </section>
          <section class="tutorial-overlay" id="tutorial-overlay" hidden aria-live="polite">
            <div class="tutorial-card">
              <span id="tutorial-step-label">Training</span>
              <h3 id="tutorial-title">Move</h3>
              <p id="tutorial-copy">Try moving now.</p>
              <div class="tutorial-hints" id="tutorial-hints"></div>
              <button class="button secondary" id="tutorial-skip-run" type="button">Exit training</button>
            </div>
          </section>
          <section class="discovery-card" id="discovery-card" hidden aria-live="polite">
            <article>
              <div class="brand-mark">New intel</div>
              <h3 id="discovery-title">Feature discovered</h3>
              <p id="discovery-copy"></p>
              <button class="button" id="discovery-dismiss" type="button">Got it</button>
            </article>
          </section>
          <section class="discovery-card" id="whats-new-card" hidden aria-live="polite">
            <article>
              <div class="brand-mark">Version ${CURRENT_APP_VERSION}</div>
              <h3>What's New</h3>
              <ul>
                <li>New onboarding training</li>
                <li>Feature discovery cards</li>
                <li>Chapter introductions</li>
                <li>Improved input guidance for desktop and mobile</li>
              </ul>
              <button class="button" id="whats-new-dismiss" type="button">Continue</button>
            </article>
          </section>
        </section>
      </main>`;
  }

  private bind(canvas: HTMLCanvasElement): void {
    this.require<HTMLFormElement>("identity-form").addEventListener("submit", (event) => {
      event.preventDefault();
      const name = this.require<HTMLInputElement>("player-name-input").value;
      const avatar = this.selectedAvatar("avatar-picker");
      this.save = this.repository.updateProfile(this.save, name, avatar);
      this.refreshProfile();
      this.show(this.postProfileScreen());
    });
    this.require("guest-button").addEventListener("click", () => {
      this.save = this.repository.updateProfile(this.save, "Guest", "cracker");
      this.refreshProfile();
      this.show(this.postProfileScreen());
    });
    this.require("start-training-button").addEventListener("click", () => this.startTutorialRun());
    this.require("skip-training-button").addEventListener("click", () => {
      this.save = this.repository.skipOnboarding(this.save);
      this.show("MAIN_MENU");
      this.maybeShowWhatsNew();
    });
    this.require("play-button").addEventListener("click", () =>
      this.startCampaignLevel(this.save.highestUnlockedLevel));
    this.require("campaign-map-button").addEventListener("click", () => this.show("CAMPAIGN_MAP"));
    this.require("endless-button").addEventListener("click", () => {
      if (this.save.endlessUnlocked) {
        this.mode = "endless";
        this.show("VAULT_SELECT");
      }
    });
    this.require("campaign-back").addEventListener("click", () => this.show("MAIN_MENU"));
    this.require("back-button").addEventListener("click", () => this.show("MAIN_MENU"));
    this.require("menu-button").addEventListener("click", () => this.show("MAIN_MENU"));
    this.require("results-map-button").addEventListener("click", () => this.show("CAMPAIGN_MAP"));
    this.require("results-leaderboard-button").addEventListener("click", () => {
      if (window.GameLeaderboard) window.GameLeaderboard.open("diamond-rush", "Diamond Dash");
    });
    this.require("details-toggle").addEventListener("click", () => this.toggleResultDetails());
    this.require("next-level-button").addEventListener("click", () => this.nextLevelFromResults());
    this.require("replay-button").addEventListener("click", () => this.replayCurrentRun());
    this.require("share-result-button").addEventListener("click", () => void this.shareResult());
    this.require("share-facebook").addEventListener("click", () => this.shareToSocial("facebook"));
    this.require("share-x").addEventListener("click", () => this.shareToSocial("x"));
    this.require("results-double-gold-button").addEventListener("click", () => void this.handleDoubleGoldOffer());
    this.require("run-end-continue-button").addEventListener("click", () => void this.handleContinueOffer());
    this.require("run-end-skip-button").addEventListener("click", () => this.declineContinueOffer());
    this.require("pause-button").addEventListener("click", () => this.pauseRun());
    this.require("pause-resume-button").addEventListener("click", () => this.resumeRun());
    this.require("pause-menu-button").addEventListener("click", () => {
      this.paused = false;
      this.pauseOverlay.hidden = true;
      this.show("MAIN_MENU");
    });
    this.require("pause-manual-button").addEventListener("click", () => {
      this.pauseOverlay.hidden = true;
      this.hud.hidden = true;
      this.require("manual-back-to-pause").hidden = false;
      this.manualScreen.hidden = false;
      this.renderManual();
    });
    this.require("manual-back-to-pause").addEventListener("click", () => {
      this.manualScreen.hidden = true;
      this.require("manual-back-to-pause").hidden = true;
      this.hud.hidden = false;
      this.pauseOverlay.hidden = false;
    });
    this.require("sound-toggle").addEventListener("click", () => this.toggleSound());
    this.require("motion-toggle").addEventListener("click", () => this.toggleMotion());
    this.require("privacy-choices").addEventListener("click", this.handlePrivacyChoices);
    this.require("profile-button").addEventListener("click", () => this.show("PROFILE"));
    this.require("home-avatar").addEventListener("click", () => this.show("PROFILE"));
    this.require("statistics-button").addEventListener("click", () => this.show("STATISTICS"));
    this.require("settings-button").addEventListener("click", () => this.show("SETTINGS"));
    this.require("manual-button").addEventListener("click", () => this.show("MANUAL"));
    this.require("settings-sound-toggle").addEventListener("click", () => this.toggleSound());
    this.require("settings-motion-toggle").addEventListener("click", () => this.toggleMotion());
    this.require("settings-replay-tutorial").addEventListener("click", () => this.startTutorialRun());
    this.require("settings-manual").addEventListener("click", () => this.show("MANUAL"));
    this.require("settings-privacy").addEventListener("click", this.handlePrivacyChoices);
    this.require("discovery-dismiss").addEventListener("click", () => {
      this.discoveryCard.hidden = true;
    });
    this.require("whats-new-dismiss").addEventListener("click", () => {
      this.save = this.repository.markWhatsNewSeen(this.save, CURRENT_APP_VERSION);
      this.whatsNewCard.hidden = true;
    });
    this.require("tutorial-skip-run").addEventListener("click", () => {
      this.tutorialActive = false;
      this.save = this.repository.skipOnboarding(this.save);
      this.show("MAIN_MENU");
      this.maybeShowWhatsNew();
    });
    this.root.querySelectorAll<HTMLElement>("[data-home]").forEach((button) => {
      button.addEventListener("click", () => this.show("MAIN_MENU"));
    });
    this.require<HTMLFormElement>("profile-form").addEventListener("submit", (event) => {
      event.preventDefault();
      const name = this.require<HTMLInputElement>("profile-name-input").value;
      const avatar = this.selectedAvatar("profile-screen");
      this.save = this.repository.updateProfile(this.save, name, avatar);
      this.renderProfile();
      this.refreshProfile();
    });
    this.root.querySelectorAll<HTMLElement>("[data-vault]").forEach((button) => {
      button.addEventListener("click", () => this.startEndless(button.dataset.vault as VaultId));
    });
    this.applySettings();

    window.addEventListener("resize", () => {
      this.renderer.resize();
      this.recomputeHudSafeTop();
      this.movement.place(this.player, this.player.position, this.arenaBounds());
      const diamondBounds = this.diamondBounds();
      this.diamonds.forEach((diamond) => {
        diamond.position.x = Math.max(diamondBounds.left, Math.min(diamondBounds.right, diamond.position.x));
        diamond.position.y = Math.max(diamondBounds.top, Math.min(diamondBounds.bottom, diamond.position.y));
      });
      const hazardBounds = this.hazardBounds();
      this.hazards.forEach((hazard) => {
        hazard.position.x = Math.max(hazardBounds.left, Math.min(hazardBounds.right, hazard.position.x));
        hazard.position.y = Math.max(hazardBounds.top, Math.min(hazardBounds.bottom, hazard.position.y));
      });
    });
    this.input.bind(canvas, (position) => {
      if (this.state === "PLAYING" && !this.paused) {
        const before = { ...this.player.position };
        this.movement.place(this.player, position, this.arenaBounds());
        if (Math.hypot(this.player.position.x - before.x, this.player.position.y - before.y) > 1) {
          this.tutorialMovementDetected = true;
        }
      }
    });
  }

  private setVault(vault: VaultId): void {
    this.vault = vault;
    this.root.dataset.vault = vault;
  }

  private startRun(vault: VaultId): void {
    if (this.mode === "endless" && !this.save.unlockedVaults.includes(vault)) return;
    this.setVault(vault);
    const startTime = this.currentLevel?.startTime ?? VAULTS[vault].time;
    this.timer = new TimerSystem(startTime);
    this.combo = new ComboSystem(this.currentLevel?.comboWindow ?? 1.5);
    this.score = 0;
    this.collected = 0;
    this.elapsed = 0;
    this.hitCooldown = 0;
    this.bonusTimeEarned = 0;
    this.shieldCharges = 0;
    this.hazardsFrozenFor = 0;
    this.freezeCooldown = 0;
    this.nextHazardSpawnIn = 0;
    this.previousAlertLevel = 0;
    this.runEnding = false;
    this.hazardCausedTimeout = false;
    this.endTransitionRemaining = 0;
    this.pendingEndReason = undefined;
    this.continueOfferPending = false;
    this.rewardedRunState.reset();
    void this.ads.loadRewardedAd("continue_run");
    void this.ads.loadRewardedAd("double_gold");
    this.bestCombo = 0;
    this.rareDiamonds = 0;
    this.shieldUses = 0;
    this.freezeUses = 0;
    this.positionHistory = [];
    this.positionSampleTimer = 0;
    this.relicUnlockedThisRun = undefined;
    this.hazardHits = 0;
    this.hazardsSpawned = 0;
    this.hazardsDestroyed = 0;
    this.maximumHeat = 0;
    this.specialDiamondsCollected = 0;
    this.endTransition.hidden = true;
    this.require<HTMLElement>("run-end-continue-offer").hidden = true;
    this.require<HTMLButtonElement>("results-double-gold-button").hidden = true;
    this.levelSucceeded = false;
    this.player.position = new Vector2(this.renderer.width / 2, this.renderer.height / 2);
    this.diamonds = [];
    this.hazards = [];
    this.particles = [];
    this.floatingTexts = [];
    this.show("PLAYING");
    this.recomputeHudSafeTop();
    const spawnTarget = this.currentLevel ? 8 : VAULTS[vault].spawnTarget;
    for (let i = 0; i < spawnTarget; i += 1) this.spawnDiamond();
    this.track("run_started", {
      mode: this.mode,
      vault,
      level_id: this.currentLevel?.id,
      difficulty: this.currentLevel ? difficultyTagFor(this.currentLevel) : "endless",
    });
    if (this.currentLevel) {
      this.track("level_started", {
        level_id: this.currentLevel.id,
        vault: this.currentLevel.vault,
        difficulty: difficultyTagFor(this.currentLevel),
      });
    }
    this.updateHud();
  }

  private startTutorialRun(): void {
    this.save = this.repository.startTutorial(this.save);
    this.tutorialActive = true;
    this.tutorialStep = "movement";
    this.tutorialStepElapsed = 0;
    this.tutorialMovementDetected = false;
    this.mode = "campaign";
    this.currentLevel = undefined;
    this.setVault("street");
    this.timer = new TimerSystem(28);
    this.combo = new ComboSystem(1.5);
    this.score = 0;
    this.collected = 0;
    this.elapsed = 0;
    this.hitCooldown = 0;
    this.bonusTimeEarned = 0;
    this.shieldCharges = 0;
    this.hazardsFrozenFor = 0;
    this.freezeCooldown = 0;
    this.runEnding = false;
    this.pendingEndReason = undefined;
    this.bestCombo = 0;
    this.rareDiamonds = 0;
    this.specialDiamondsCollected = 0;
    this.hazardsSpawned = 0;
    this.hazardsDestroyed = 0;
    this.hazardHits = 0;
    this.maximumHeat = 0;
    this.player.position = new Vector2(this.renderer.width / 2, this.renderer.height / 2);
    this.diamonds = [];
    this.hazards = [];
    this.particles = [];
    this.floatingTexts = [];
    this.endTransition.hidden = true;
    this.show("PLAYING");
    this.recomputeHudSafeTop();
    this.track("tutorial_started", { tutorial: "training_run" });
    this.advanceTutorial("movement");
    this.updateHud();
  }

  private show(state: GameState): void {
    this.state = state;
    if (state !== "MAIN_MENU") {
      this.discoveryCard.hidden = true;
      this.whatsNewCard.hidden = true;
    }
    document.body.classList.toggle("gameplay-active", state === "PLAYING");
    this.onboarding.hidden = state !== "ONBOARDING";
    this.onboardingEntry.hidden = state !== "ONBOARDING_ENTRY";
    this.menu.hidden = state !== "MAIN_MENU";
    this.profileScreen.hidden = state !== "PROFILE";
    this.statisticsScreen.hidden = state !== "STATISTICS";
    this.settingsScreen.hidden = state !== "SETTINGS";
    this.manualScreen.hidden = state !== "MANUAL";
    this.campaignMap.hidden = state !== "CAMPAIGN_MAP";
    this.vaultScreen.hidden = state !== "VAULT_SELECT";
    this.resultScreen.hidden = state !== "RESULTS";
    this.hud.hidden = state !== "PLAYING";
    if (state !== "PLAYING") this.require("tutorial-overlay").hidden = true;
    if (state !== "PLAYING") {
      this.pauseOverlay.hidden = true;
      this.paused = false;
    }
    if (state !== "PLAYING") this.frame.classList.remove("low-time");
    if (state === "MAIN_MENU") {
      this.ads.showPlacement("menu");
      this.trackVisibleAdPlacement("menu");
    } else if (state === "CAMPAIGN_MAP") {
      this.ads.showPlacement("campaign");
      this.trackVisibleAdPlacement("campaign");
    } else this.ads.hideAll();
    if (state === "ONBOARDING_ENTRY") this.renderOnboardingEntry();
    if (state === "MAIN_MENU") {
      this.refreshProfile();
      this.maybeShowWhatsNew();
    }
    if (state === "PROFILE") this.renderProfile();
    if (state === "STATISTICS") this.renderStatistics();
    if (state === "MANUAL") this.renderManual();
    if (state === "CAMPAIGN_MAP") this.renderCampaignMap();
    if (state === "VAULT_SELECT") this.refreshVaults();
  }

  private pauseRun(): void {
    if (this.state !== "PLAYING" || this.runEnding || this.tutorialActive) return;
    this.paused = true;
    this.pauseOverlay.hidden = false;
    this.track("run_paused", { mode: this.mode, vault: this.vault });
  }

  private resumeRun(): void {
    this.paused = false;
    this.pauseOverlay.hidden = true;
  }

  private loop = (now: number): void => {
    this.clock.tick(now, (stepSeconds) => {
      if (this.state === "PLAYING" && !this.paused) {
        this.elapsed += stepSeconds;
        this.update(stepSeconds);
      }
    });
    this.render();
    requestAnimationFrame(this.loop);
  };

  private update(delta: number): void {
    if (this.tutorialActive) {
      this.updateTutorial(delta);
      return;
    }
    if (this.runEnding) {
      this.updateEffects(delta);
      this.endTransitionRemaining -= delta;
      if (this.endTransitionRemaining <= 0 && this.pendingEndReason) {
        this.completeRun(this.pendingEndReason);
      }
      return;
    }
    const endless = this.mode === "endless" ? endlessDifficulty(this.elapsed) : undefined;
    this.timer.update(delta * (endless?.timerDrainScale ?? 1));
    if (endless) {
      this.combo.setWindow(endless.comboWindow);
    }
    const alert = this.currentAlert();
    this.updateHazardPressure(delta, alert);
    this.combo.update(delta);
    this.hitCooldown = Math.max(0, this.hitCooldown - delta);
    this.shakeAmount = Math.max(0, this.shakeAmount - delta * 46);
    this.hazardsFrozenFor = Math.max(0, this.hazardsFrozenFor - delta);
    this.freezeCooldown = Math.max(0, this.freezeCooldown - delta);
    this.updateKeyboardMovement(delta);
    this.samplePlayerPosition(delta);
    this.updateEffects(delta);
    const jammerActive = this.hazardSystem.insideJammer(this.player, this.hazards);
    const baseComboWindow = (this.currentLevel?.comboWindow ?? endless?.comboWindow ?? 1.5)
      + relicBonuses(this.save.relics).comboWindow;
    this.combo.setWindow(Math.max(0.75, baseComboWindow - (jammerActive ? 0.25 : 0)));

    for (const diamond of this.diamonds) {
      diamond.pulse += delta * 4;
      if (this.collisions.overlaps(
        this.player,
        diamond,
        this.player.radius * (
          1
          + relicBonuses(this.save.relics).pickupRadius
        ) * (jammerActive ? 0.55 : 1),
      )) this.collect(diamond);
    }

    for (const hazard of this.hazards) {
      this.hazardSystem.update(
        hazard,
        delta,
        this.hazardBounds(),
        alert.hazardSpeedScale
          * relicBonuses(this.save.relics).hazardSpeedScale,
        this.hazardsFrozenFor > 0,
        this.player,
      );
      if (this.hitCooldown === 0 && this.hazardSystem.overlaps(this.player, hazard)) {
        const shield = this.specialDiamondSystem.consumeShield(this.shieldCharges);
        this.shieldCharges = shield.charges;
        if (shield.blocked) {
          this.shieldUses += 1;
          this.showSpecialPopup(this.player.position, "Shield Blocked", "#b8a6ff");
          this.vibrate(20);
        } else {
          this.hazardHits += 1;
          this.timer.update(alert.hazardPenalty);
          this.combo.break();
          this.showSpecialPopup(
            this.player.position,
            `-${alert.hazardPenalty.toFixed(1)} Sec`,
            "#ff7895",
          );
          if (this.timer.expired) this.hazardCausedTimeout = true;
          this.triggerShake(14);
          this.vibrate([50, 30, 50]);
        }
        this.hitCooldown = 0.8;
        this.audio.play("hazard");
      }
    }

    this.updateHud();
    if (this.currentLevel && objectiveComplete(this.currentLevel.objective, this.objectiveStats())) {
      this.levelSucceeded = true;
      this.startEndTransition("target_complete");
    } else if (this.timer.expired) {
      this.startEndTransition(this.hazardCausedTimeout ? "hazard_timeout" : "timer_expired");
    }
  }

  private updateTutorial(delta: number): void {
    this.tutorialStepElapsed += delta;
    this.timer.update(delta * 0.35);
    this.combo.update(delta);
    this.hitCooldown = Math.max(0, this.hitCooldown - delta);
    this.hazardsFrozenFor = Math.max(0, this.hazardsFrozenFor - delta);
    this.updateKeyboardMovement(delta);
    this.updateEffects(delta);
    for (const diamond of this.diamonds) {
      diamond.pulse += delta * 4;
      if (this.collisions.overlaps(this.player, diamond, this.player.radius * 1.25)) {
        this.collect(diamond);
      }
    }
    for (const hazard of this.hazards) {
      this.hazardSystem.update(hazard, delta, this.hazardBounds(), 0.45, this.hazardsFrozenFor > 0, this.player);
    }
    if (this.tutorialStep === "movement" && this.tutorialMovementDetected) {
      this.advanceTutorial("diamond");
    } else if (this.tutorialStep === "diamond" && this.collected >= 1) {
      this.advanceTutorial("timer");
    } else if (this.tutorialStep === "timer" && this.collected >= 2) {
      this.advanceTutorial("combo");
    } else if (
      this.tutorialStep === "combo"
      && (this.combo.count >= 2 || this.tutorialStepElapsed >= 10)
    ) {
      this.advanceTutorial("hazard");
    } else if (this.tutorialStep === "hazard" && this.tutorialStepElapsed >= 5) {
      this.advanceTutorial("special");
    } else if (this.tutorialStep === "special" && this.specialDiamondsCollected >= 1) {
      this.advanceTutorial("objective");
    } else if (this.tutorialStep === "objective" && this.collected >= 8) {
      this.completeTutorialRun();
    }
    this.updateHud();
  }

  private advanceTutorial(step: TutorialStep): void {
    this.tutorialStep = step;
    this.tutorialStepElapsed = 0;
    this.hazards = [];
    const overlay = this.require("tutorial-overlay");
    overlay.hidden = false;
    const controlCopy = controlCopyFor(this.platformContext());
    const setText = (title: string, copy: string, hints: string[] = []): void => {
      this.require("tutorial-title").textContent = title;
      this.require("tutorial-copy").textContent = copy;
      this.require("tutorial-hints").innerHTML = hints
        .map((hint) => `<span>${hint}</span>`)
        .join("");
      this.require("tutorial-step-label").textContent = `Training - ${step}`;
    };

    if (step === "movement") {
      this.diamonds = [];
      setText(controlCopy.title, controlCopy.body, controlCopy.hints);
      return;
    }
    if (step === "diamond") {
      this.diamonds = [this.tutorialDiamond("SHARD", 80, 0)];
      setText("Collect Diamond", "Collect the highlighted diamond.", ["Diamonds add score", "Move through it"]);
      return;
    }
    if (step === "timer") {
      this.diamonds = [this.tutorialDiamond("CUT_DIAMOND", -85, -20), this.tutorialDiamond("SHARD", 90, 30)];
      setText("Timer", "Diamonds add time. Keep collecting to stay inside the vault.", ["Timer hits zero = run ends"]);
      return;
    }
    if (step === "combo") {
      this.diamonds = [
        this.tutorialDiamond("SHARD", -90, 0),
        this.tutorialDiamond("SHARD", 0, -70),
        this.tutorialDiamond("CUT_DIAMOND", 90, 0),
      ];
      setText("Combo", "Collect quickly to build combos.", ["Fast chains multiply score"]);
      return;
    }
    if (step === "hazard") {
      this.spawnHazard(24, "laser");
      setText("Security Hazard", "Avoid security hazards. They cost time in real vaults.", ["Watch the warning line"]);
      return;
    }
    if (step === "special") {
      this.diamonds = [this.tutorialDiamond("SHIELD_DIAMOND", 0, -85)];
      setText("Special Diamond", "Shield Diamond blocks one hazard hit.", ["Purple = protection"]);
      return;
    }
    if (step === "objective") {
      this.diamonds = [
        this.tutorialDiamond("SHARD", -110, -60),
        this.tutorialDiamond("SHARD", 0, -95),
        this.tutorialDiamond("CUT_DIAMOND", 115, -50),
        this.tutorialDiamond("SHARD", -80, 70),
        this.tutorialDiamond("CUT_DIAMOND", 80, 80),
      ];
      setText("Objective", "Complete the objective before time runs out. Collect 8 total diamonds.", ["Training target: 8 diamonds"]);
      return;
    }
  }

  private tutorialDiamond(type: Diamond["type"], offsetX: number, offsetY: number): Diamond {
    const cfg = DIAMONDS[type];
    const bounds = this.diamondBounds();
    return {
      id: Date.now() + Math.floor(Math.random() * 10000),
      type,
      radius: cfg.size,
      pulse: 0,
      position: new Vector2(
        Math.max(bounds.left, Math.min(bounds.right, this.player.position.x + offsetX)),
        Math.max(bounds.top, Math.min(bounds.bottom, this.player.position.y + offsetY)),
      ),
    };
  }

  private completeTutorialRun(): void {
    this.tutorialActive = false;
    this.require("tutorial-overlay").hidden = true;
    this.save = this.repository.completeTutorial(this.save);
    this.refreshProfile();
    this.show("MAIN_MENU");
    this.track("tutorial_completed", { tutorial: "training_run" });
    this.showDiscoveryCard("training_complete", "Training Complete", "+100 Gold. You're ready to crack real vaults.");
    this.maybeShowWhatsNew();
  }

  private collect(diamond: Diamond): void {
    const cfg = DIAMONDS[diamond.type];
    const collectionPoint = { ...diamond.position };
    const previousMultiplier = this.combo.multiplier;
    const multiplier = this.combo.collect();
    this.bestCombo = Math.max(this.bestCombo, this.combo.count);
    const heat = this.currentHeat();
    const scoreGain = Math.round(
      scoreFor(cfg.score, multiplier)
      * (this.currentLevel?.scoreScale ?? 1)
      * heat.scoreScale,
    );
    this.score += scoreGain;
    this.collected += 1;
    if (["FLAWLESS_DIAMOND", "SYNDICATE_GEM", "CROWN_JEWEL"].includes(diamond.type)) {
      this.rareDiamonds += 1;
    }
    if (["CLEAN_DIAMOND", "FREEZE_DIAMOND", "SHIELD_DIAMOND", "TIME_DIAMOND"].includes(diamond.type)) {
      this.specialDiamondsCollected += 1;
    }
    const endless = this.mode === "endless" ? endlessDifficulty(this.elapsed) : undefined;
    const timeScale = this.currentLevel?.timeRewardScale ?? endless?.timeRewardScale ?? 1;
    const timeAward = (diamond.type === "TIME_DIAMOND" ? 5 : cfg.time * timeScale)
      * relicBonuses(this.save.relics).timeScale;
    this.timer.add(timeAward);
    this.bonusTimeEarned += timeAward;
    this.applySpecialDiamond(diamond, collectionPoint);
    this.audio.play("collect");
    this.vibrate(10);
    this.spawnCollectionEffect(collectionPoint, cfg.color, scoreGain, timeAward);
    if (multiplier > previousMultiplier) {
      this.floatingTexts.push({
        position: { x: collectionPoint.x, y: collectionPoint.y - 34 },
        text: `COMBO x${multiplier}`,
        color: "#ffd166",
        life: 1.1,
        maxLife: 1.1,
      });
      this.bumpCombo();
      this.audio.play("combo");
      this.vibrate([15, 40, 15]);
    }
    if (this.tutorialActive) {
      this.diamonds = this.diamonds.filter((candidate) => candidate !== diamond);
      return;
    }
    const alert = this.currentAlert();
    this.spawner.respawn(
      diamond,
      this.diamondBounds(),
      this.player.position,
      110,
      alert.level,
      this.freezeCooldown > 0,
      heat.rareChanceBonus + relicBonuses(this.save.relics).rareChanceBonus,
      this.currentLevel?.rewardPool,
    );
  }

  private startEndTransition(endReason: NonNullable<RunResult["endReason"]>): void {
    if (this.runEnding) return;
    this.runEnding = true;
    this.pendingEndReason = endReason;
    const offerContinue = Boolean(
      endReason !== "target_complete"
      && this.rewardedRunState.canOfferContinue()
      && this.ads.isRewardedAdReady("continue_run"),
    );
    this.continueOfferPending = offerContinue;
    // Pause the automatic hand-off to completeRun() while a continue offer is on
    // screen - the player's explicit choice (watch ad / end run) resolves it instead.
    this.endTransitionRemaining = offerContinue
      ? Number.POSITIVE_INFINITY
      : endReason === "target_complete" ? 1.35 : 1.65;
    const message = runTransitionMessageFor(
      endReason,
      this.currentLevel ? objectiveName(this.currentLevel.objective) : undefined,
    );
    this.require("run-end-title").textContent = message.title;
    this.require("run-end-detail").textContent = message.detail;
    this.endTransition.classList.toggle("success", endReason === "target_complete");
    this.endTransition.classList.toggle("failure", endReason !== "target_complete");
    const continueOffer = this.require<HTMLElement>("run-end-continue-offer");
    continueOffer.hidden = !offerContinue;
    this.setContinueOfferButtonsEnabled(true);
    this.endTransition.hidden = false;
    if (offerContinue) {
      this.track("rewarded_ad_offered", {
        placement: "continue_run",
        level_id: this.currentLevel?.id,
        end_reason: endReason,
      });
      this.showDiscoveryCard("continue_run_ad");
    }
  }

  private setContinueOfferButtonsEnabled(enabled: boolean): void {
    this.require<HTMLButtonElement>("run-end-continue-button").disabled = !enabled;
    this.require<HTMLButtonElement>("run-end-skip-button").disabled = !enabled;
  }

  private async handleContinueOffer(): Promise<void> {
    if (!this.continueOfferPending || !this.pendingEndReason) return;
    const reason = this.pendingEndReason;
    this.setContinueOfferButtonsEnabled(false);
    this.require("run-end-detail").textContent = "Loading ad...";
    const result = await this.ads.showRewardedAd("continue_run");
    // Guard against the run already having ended some other way while the ad played.
    if (!this.continueOfferPending || this.pendingEndReason !== reason) return;
    if (result.rewarded && this.rewardedRunState.useContinue()) {
      this.continueOfferPending = false;
      this.save = this.repository.recordRewardedAd(this.save, 0);
      this.track("rewarded_ad_completed", {
        placement: "continue_run",
        level_id: this.currentLevel?.id,
      });
      this.applyContinueBonus();
      this.resumeRunAfterContinue();
      return;
    }
    this.track("rewarded_ad_declined", {
      placement: "continue_run",
      level_id: this.currentLevel?.id,
      reason: result.rewarded ? "already_used" : "not_rewarded",
    });
    this.continueOfferPending = false;
    this.completeRun(reason);
  }

  private declineContinueOffer(): void {
    if (!this.continueOfferPending || !this.pendingEndReason) return;
    const reason = this.pendingEndReason;
    this.continueOfferPending = false;
    this.track("rewarded_ad_skipped", { placement: "continue_run", level_id: this.currentLevel?.id });
    this.completeRun(reason);
  }

  private applyContinueBonus(): void {
    this.timer.add(12);
    this.hazardsFrozenFor = Math.max(this.hazardsFrozenFor, 3);
    this.hitCooldown = Math.max(this.hitCooldown, 1.5);
    this.hazardCausedTimeout = false;
    this.showSpecialPopup(this.player.position, "Run Continued +12s", "#7cffb2");
  }

  private resumeRunAfterContinue(): void {
    this.runEnding = false;
    this.pendingEndReason = undefined;
    this.endTransitionRemaining = 0;
    this.endTransition.hidden = true;
    this.require<HTMLElement>("run-end-continue-offer").hidden = true;
  }

  private completeRun(endReason: NonNullable<RunResult["endReason"]>): void {
    this.pendingEndReason = undefined;
    this.endTransition.hidden = true;
    const heat = this.currentHeat();
    const xp = Math.round(xpFor(this.score, this.collected) * heat.xpScale);
    const gold = Math.max(
      1,
      Math.floor(
        this.score / 75
        * (1 + heat.tier * 0.05)
        * relicBonuses(this.save.relics).goldScale,
      ),
    );
    const result: RunResult = {
      score: this.score,
      diamonds: this.collected,
      gold,
      xp,
      isHighScore: this.score > this.save.highScore,
      mode: this.mode,
      level: this.currentLevel?.id,
      success: this.currentLevel ? this.levelSucceeded : true,
      endReason,
      stars: this.currentLevel && this.levelSucceeded
        ? starsForTime(this.timer.remaining, this.currentLevel.startTime)
        : 0,
    };
    this.lastResult = result;
    this.track("run_ended", {
      mode: result.mode,
      vault: this.vault,
      level_id: result.level,
      success: result.success,
      end_reason: result.endReason,
      score: result.score,
      diamonds: result.diamonds,
      gold: result.gold,
      xp: result.xp,
      stars: result.stars,
      duration_seconds: Math.round(this.elapsed),
      best_combo: this.bestCombo,
      heat: Math.round(this.maximumHeat),
    });
    if (result.level) {
      this.track(result.success ? "level_completed" : "level_failed", {
        level_id: result.level,
        vault: this.currentLevel?.vault,
        end_reason: result.endReason,
        score: result.score,
        stars: result.stars,
        duration_seconds: Math.round(this.elapsed),
      });
    }
    const previousHighScore = this.save.highScore;
    const previousBestCombo = this.save.statistics.bestCombo;
    const previousHeat = this.save.statistics.highestHeat;
    const previousLongestRun = this.save.statistics.longestSurvivalSeconds;
    this.save = this.repository.completeRun(
      this.save,
      result.score,
      result.diamonds,
      result.gold,
      result.xp,
      {
        bestCombo: this.bestCombo,
        survivalSeconds: this.elapsed,
        highestHeat: this.maximumHeat,
        masterVaultWin: Boolean(this.currentLevel?.master && result.success),
        rareGems: this.rareDiamonds,
        hazardsAvoided: Math.max(0, this.hazardsSpawned - this.hazardHits),
        hazardsDestroyed: this.hazardsDestroyed,
        specialDiamonds: this.specialDiamondsCollected,
        endlessScore: this.mode === "endless" ? this.score : 0,
      },
    );
    this.track("reward_claimed", {
      level_id: result.level,
      vault: this.currentLevel?.vault,
      score: result.score,
      duration_seconds: Math.round(this.elapsed),
    });
    if (this.mode === "campaign" && this.currentLevel && result.success) {
      const pendingRelic = this.currentLevel.master ? relicForVault(this.currentLevel.vault) : undefined;
      const shouldUnlockRelic = Boolean(pendingRelic && !this.save.relics.includes(pendingRelic.id));
      this.save = this.repository.completeCampaignLevel(this.save, this.currentLevel.id, result.stars ?? 1);
      if (this.currentLevel.id >= 10) this.showDiscoveryCard("endless_rush");
      if (this.currentLevel.master) {
        const relic = relicForVault(this.currentLevel.vault);
        if (shouldUnlockRelic) {
          this.save = this.repository.unlockRelic(this.save, relic.id);
          this.showDiscoveryCard("relics");
          this.relicUnlockedThisRun = `${relic.name} unlocked - ${relic.description}`;
        }
      }
    }
    this.audio.play(result.success ? "resultsWin" : "results");
    this.results.show(result, VAULTS[this.vault].name);
    this.require("result-level").textContent = this.currentLevel
      ? `Level ${this.currentLevel.id} - ${this.currentLevel.master ? "Master Vault" : this.currentLevel.vaultName}`
      : `Endless stage ${endlessDifficulty(this.elapsed).stage}`;
    const resultObjective = this.require("result-objective");
    if (this.currentLevel) {
      const stats = this.objectiveStats();
      const objective = this.currentLevel.objective;
      const achieved = objectiveValue(objective, stats);
      const met = objectiveComplete(objective, stats);
      resultObjective.innerHTML = `Target: <strong>${objectiveName(objective)}</strong> - ${
        met ? "done" : `reached ${achieved} of ${objective.target}`
      }`;
      resultObjective.dataset.met = String(met);
    } else {
      resultObjective.textContent = "";
      delete resultObjective.dataset.met;
    }
    this.require("result-stars").textContent = this.currentLevel && result.success
      ? `${result.stars ?? 1} / 3 stars`
      : "";
    this.require("result-relic").textContent = this.relicUnlockedThisRun ?? "";
    this.require("result-combo").textContent = `x${this.bestCombo}`;
    this.require("result-heat").textContent = String(Math.round(this.maximumHeat));
    this.require("result-primary-score").textContent = result.score.toLocaleString();
    if (window.GameLeaderboard) {
      window.GameLeaderboard.recordScore("diamond-rush", {
        score: result.score,
        level: this.currentLevel?.id ?? 0,
      });
    }
    this.require("result-primary-stars").textContent = this.currentLevel
      ? (result.success ? `${result.stars ?? 1}/3` : "0/3")
      : "Endless";
    this.require("result-primary-gold").textContent = `+${result.gold}`;
    const badges = [
      this.score > previousHighScore ? "New High Score" : "",
      this.bestCombo > previousBestCombo ? "Best Combo" : "",
      this.maximumHeat > previousHeat ? "Heat Record" : "",
      this.elapsed > previousLongestRun ? "Longest Run" : "",
    ].filter(Boolean);
    this.require("record-badges").innerHTML =
      badges.map((badge) => `<span>${badge}</span>`).join("");
    this.require("so-close").textContent = this.soCloseMessage(result);
    this.require<HTMLButtonElement>("results-map-button").hidden = this.mode !== "campaign";
    const canAdvance = Boolean(
      this.mode === "campaign"
      && this.currentLevel
      && result.success
      && this.currentLevel.id < 30,
    );
    this.require<HTMLButtonElement>("next-level-button").hidden = !canAdvance;
    this.require("replay-button").textContent = this.currentLevel ? "Replay level" : "Run it again";
    this.require<HTMLButtonElement>("replay-button").hidden = false;
    this.prepareResultDetails(result, badges.length);
    this.prepareDoubleGoldOffer(result);
    this.show("RESULTS");
    this.resultScreen.scrollTop = 0;
  }

  private prepareDoubleGoldOffer(result: RunResult): void {
    const button = this.require<HTMLButtonElement>("results-double-gold-button");
    const canOffer = Boolean(
      result.success
      && result.gold > 0
      && this.rewardedRunState.canOfferDoubleGold()
      && this.ads.isRewardedAdReady("double_gold"),
    );
    button.hidden = !canOffer;
    button.disabled = false;
    button.textContent = `Watch ad: Double gold (+${result.gold})`;
    if (canOffer) {
      this.track("rewarded_ad_offered", {
        placement: "double_gold",
        level_id: result.level,
        bonus_gold: result.gold,
      });
      this.showDiscoveryCard("double_gold_ad");
    }
  }

  private async handleDoubleGoldOffer(): Promise<void> {
    const result = this.lastResult;
    if (!result || !this.rewardedRunState.canOfferDoubleGold()) return;
    const button = this.require<HTMLButtonElement>("results-double-gold-button");
    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = "Loading ad...";
    const adResult = await this.ads.showRewardedAd("double_gold");
    if (this.lastResult !== result) return; // A new run started while the ad was playing.
    if (adResult.rewarded && this.rewardedRunState.claimDoubleGold()) {
      this.save = this.repository.recordRewardedAd(this.save, result.gold);
      this.track("rewarded_ad_completed", {
        placement: "double_gold",
        level_id: result.level,
        bonus_gold: result.gold,
      });
      this.require("result-primary-gold").textContent = `+${result.gold * 2}`;
      button.textContent = "Gold doubled!";
      button.hidden = true;
      this.refreshProfile();
      return;
    }
    this.track("rewarded_ad_declined", { placement: "double_gold", level_id: result.level });
    button.disabled = false;
    button.textContent = originalLabel;
  }

  private prepareResultDetails(result: RunResult, badgeCount: number): void {
    const details = this.require("result-details");
    const toggle = this.require<HTMLButtonElement>("details-toggle");
    const shouldOpen = Boolean(
      result.isHighScore
      || badgeCount >= 2
      || !result.success
      || (this.currentLevel && this.currentLevel.id > 5),
    );
    details.hidden = !shouldOpen;
    toggle.textContent = shouldOpen ? "Hide details" : "View details";
  }

  private toggleResultDetails(): void {
    const details = this.require("result-details");
    details.hidden = !details.hidden;
    this.require("details-toggle").textContent = details.hidden ? "View details" : "Hide details";
  }

  private trackVisibleAdPlacement(placement: "menu" | "campaign"): void {
    const element = this.root.querySelector<HTMLElement>(`[data-ad-placement="${placement}"]`);
    if (!element || element.hidden) return;
    this.track("ad_placeholder_viewed", {
      placement,
      format: placement === "menu" ? "desktop-left-side-rail" : "responsive-banner",
    });
  }

  private updateHud(): void {
    this.scoreHud.textContent = this.score.toLocaleString();
    this.timeHud.textContent = this.timer.remaining.toFixed(1);
    this.comboHud.innerHTML = `<span class="hud-label">Combo ${this.combo.count}</span><strong>x${this.combo.multiplier}</strong>`;
    this.comboHud.classList.toggle("active", this.combo.count >= 2);
    const startTime = this.currentLevel?.startTime ?? VAULTS[this.vault].time;
    const ratio = Math.min(1, this.timer.remaining / startTime);
    this.timeFill.style.transform = `scaleX(${ratio})`;
    this.timeFill.classList.toggle("danger", this.timer.overtime);
    this.frame.classList.toggle("low-time", this.timer.overtime);
    const warning = this.require("low-time-warning");
    warning.textContent = `Run ends in ${this.timer.remaining.toFixed(1)}s`;
    warning.classList.toggle("active", this.timer.overtime);
    if (this.timer.overtime) this.audio.play("lowTime");
    const endless = this.mode === "endless" ? endlessDifficulty(this.elapsed) : undefined;
    const alert = this.currentAlert();
    const objectiveCopy = this.require("objective-copy");
    if (this.tutorialActive) {
      objectiveCopy.textContent = this.tutorialObjectiveCopy();
      const progress = this.tutorialStep === "objective" ? Math.min(1, this.collected / 8) : 1;
      this.objectiveProgress.style.transform = `scaleX(${progress})`;
    } else if (this.currentLevel) {
      const masterRule = masterRuleName(this.currentLevel.masterRule);
      this.difficultyHud.textContent = `Level ${this.currentLevel.id} - ${difficultyTagFor(this.currentLevel)}`;
      this.difficultyHud.dataset.difficulty = this.currentLevel.difficulty.toLowerCase();
      objectiveCopy.textContent = [
        securityBriefFor(this.currentLevel),
        masterRule,
        objectiveText(this.currentLevel.objective, this.objectiveStats()),
      ].filter(Boolean).join(" - ");
      this.objectiveProgress.style.transform =
        `scaleX(${objectiveProgress(this.currentLevel.objective, this.objectiveStats())})`;
    } else {
      this.difficultyHud.textContent = `Endless Stage ${endless?.stage ?? 1}`;
      this.difficultyHud.dataset.difficulty = "endless";
      objectiveCopy.textContent = `Survive Stage ${endless?.stage ?? 1} - Timer 0 ends the run`;
      this.objectiveProgress.style.transform = "scaleX(1)";
    }
    const heat = this.currentHeat();
    this.alertHud.classList.toggle("lockdown", alert.level >= 6);
    this.require("alert-label").textContent = alert.level <= 0 ? "Warmup" : heat.label;
    this.shieldHud.textContent = this.shieldCharges > 0 ? "Shield ready" : "Shield empty";
    this.shieldHud.classList.toggle("ready", this.shieldCharges > 0);
    this.threatFill.style.transform = `scaleX(${alert.threatSegments / 10})`;
    this.maximumHeat = Math.max(this.maximumHeat, heat.heat);
    this.heatHud.dataset.tier = String(heat.tier);
    this.require("heat-label").textContent =
      `Heat ${heat.label} - +${Math.round((heat.scoreScale - 1) * 100)}% score - ${heat.reason}`;
    this.heatFill.style.transform = `scaleX(${heat.heat / 100})`;
    this.require("freeze-hud").textContent = this.hazardsFrozenFor > 0
      ? `Freeze ${this.hazardsFrozenFor.toFixed(1)}s`
      : "Freeze inactive";
    this.require("counterplay-hud").textContent = this.hazardsFrozenFor > 0
      ? "Hazards frozen"
      : this.shieldCharges > 0
        ? "Shield ready"
        : "Find special diamonds for help";
  }

  private tutorialObjectiveCopy(): string {
    if (this.tutorialStep === "objective") return `Training objective - ${this.collected}/8 diamonds`;
    return `Training - ${this.tutorialStep}`;
  }

  activeUpgradeSummary(): string {
    const effects: string[] = ["Special diamonds"];
    if (false) effects.push("Magnet");
    if (false) effects.push("Time+");
    if (false) effects.push("Combo+");
    if (false) effects.push("Jammer");
    if (false) effects.push("Shield");
    return effects.length ? effects.join(" - ") : "Special diamonds";
  }

  private soCloseMessage(result: RunResult): string {
    if (result.success || !this.currentLevel) return "";
    const progress = objectiveProgress(this.currentLevel.objective, this.objectiveStats());
    return progress >= 0.75
      ? `So close - ${Math.round(progress * 100)}% of the objective completed.`
      : "";
  }

  private render(): void {
    if (this.state !== "PLAYING") {
      this.renderer.drawIdle(this.elapsed);
      return;
    }
    this.renderer.drawBackground(this.elapsed, this.vault);
    const ctx = this.renderer.context;
    const shaking = this.shakeAmount > 0.1;
    if (shaking) {
      ctx.save();
      ctx.translate((Math.random() - 0.5) * this.shakeAmount, (Math.random() - 0.5) * this.shakeAmount);
    }
    this.diamonds.forEach((diamond) => this.renderer.drawDiamond(diamond));
    this.hazards.forEach((hazard) => this.renderer.drawHazard(hazard, this.elapsed));
    this.renderer.drawEffects(this.particles, this.floatingTexts);
    this.renderer.drawPlayer(this.player.position, this.player.radius, this.hitCooldown > 0, this.shieldCharges > 0);
    if (shaking) ctx.restore();
  }

  private spawnDiamond(): void {
    const alert = this.currentAlert();
    const heat = this.currentHeat();
    this.diamonds.push(this.spawner.create(
      this.diamondBounds(),
      alert.level,
      this.freezeCooldown > 0,
      heat.rareChanceBonus + relicBonuses(this.save.relics).rareChanceBonus,
      this.currentLevel?.rewardPool,
    ));
  }

  private spawnHazard(
    forcedSpeed?: number,
    type: NonNullable<Hazard["type"]> = "drone",
  ): void {
    if (type === "tracker") this.showDiscoveryCard("tracking_drone");
    if (type === "hunter") this.showDiscoveryCard("hunter_laser");
    if (type === "mine") this.showDiscoveryCard("pulse_mine");
    if (type === "jammer") this.showDiscoveryCard("jammer_field");
    const speed = forcedSpeed ?? (this.vault === "bank" ? 80 : 65);
    const angle = Math.random() * Math.PI * 2;
    const predicted = type === "hunter" ? this.predictedPlayerPoint() : undefined;
    const orientation = predicted
      ? (Math.abs(predicted.x - this.player.position.x) > Math.abs(predicted.y - this.player.position.y)
        ? "vertical"
        : "horizontal")
      : Math.random() < 0.5 ? "horizontal" : "vertical";
    const laserVelocity = orientation === "horizontal"
      ? { x: 0, y: (Math.random() < 0.5 ? -1 : 1) * speed * 0.45 }
      : { x: (Math.random() < 0.5 ? -1 : 1) * speed * 0.45, y: 0 };
    this.hazards.push({
      id: this.nextHazardId++,
      type,
      position: predicted ?? this.hazardSpawnPoint(),
      target: predicted,
      velocity: type === "laser"
        ? laserVelocity
        : type === "mine" || type === "jammer" || type === "hunter"
          ? { x: 0, y: 0 }
          : { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
      radius: type === "jammer" ? 76 : type === "mine" ? 32
        : type === "laser" || type === "hunter" ? 7 : 17,
      age: Math.random() * 0.5,
      phase: "warning",
      orientation,
      length: type === "laser" || type === "hunter"
        ? Math.min(420, Math.max(230, this.renderer.width * 0.58))
        : undefined,
    });
    this.hazardsSpawned += 1;
  }

  private currentHeat(): SecurityHeatState {
    return securityHeatFor({
      elapsed: this.elapsed,
      collected: this.collected,
      bestCombo: this.bestCombo,
      bonusTimeEarned: this.bonusTimeEarned,
    });
  }

  private samplePlayerPosition(delta: number): void {
    this.positionSampleTimer -= delta;
    if (this.positionSampleTimer > 0) return;
    this.positionSampleTimer = 0.4;
    this.positionHistory.push({ ...this.player.position });
    this.positionHistory = this.positionHistory.slice(-3);
  }

  private predictedPlayerPoint(): Point {
    const bounds = this.hazardBounds();
    if (this.positionHistory.length < 2) return { ...this.player.position };
    const previous = this.positionHistory[this.positionHistory.length - 2];
    const latest = this.positionHistory[this.positionHistory.length - 1];
    return {
      x: Math.max(bounds.left, Math.min(bounds.right, latest.x + (latest.x - previous.x) * 1.35)),
      y: Math.max(bounds.top, Math.min(bounds.bottom, latest.y + (latest.y - previous.y) * 1.35)),
    };
  }

  private currentAlert(): AlertState {
    if (this.currentLevel?.hazards === 0) {
      return {
        level: 0,
        pressure: 0,
        label: "Training",
        hazardTarget: 0,
        hazardPenalty: 0,
        hazardSpeedScale: 0,
        hazardSpawnInterval: 99,
        threatSegments: 0,
      };
    }
    const campaignHazardBonus = this.currentLevel
      ? Math.min(2, Math.floor(Math.max(0, this.currentLevel.hazards - 1) / 2))
      : 0;
    const alert = alertStateFor(this.elapsed, this.bonusTimeEarned, campaignHazardBonus);
    if (!this.currentLevel) return alert;
    return {
      ...alert,
      hazardTarget: Math.min(this.currentLevel.hazards, alert.hazardTarget),
      hazardSpawnInterval: Math.max(1.25, alert.hazardSpawnInterval),
    };
  }

  private objectiveStats(): ObjectiveStats {
    return {
      diamonds: this.collected,
      bestCombo: this.bestCombo,
      rareDiamonds: this.rareDiamonds,
    };
  }

  private updateHazardPressure(delta: number, alert: AlertState): void {
    if (alert.level <= 0) {
      this.hazards = [];
      this.previousAlertLevel = 0;
      return;
    }
    if (alert.level !== this.previousAlertLevel) {
      this.previousAlertLevel = alert.level;
      this.nextHazardSpawnIn = 0;
      this.showSpecialPopup(
        { x: this.renderer.width / 2, y: 145 },
        alert.label,
        alert.level >= 6 ? "#ff4d73" : "#ffd166",
      );
    }
    this.nextHazardSpawnIn -= delta;
    if (this.hazards.length < alert.hazardTarget && this.nextHazardSpawnIn <= 0) {
      this.spawnHazard(this.currentLevel?.hazardSpeed, this.nextHazardType(alert.level));
      this.nextHazardSpawnIn = alert.hazardSpawnInterval;
    }
  }

  private nextHazardType(alertLevel: number): NonNullable<Hazard["type"]> {
    const rule = this.currentLevel?.masterRule;
    if (rule === "laser_grid") return "laser";
    if (rule === "minefield") return "mine";
    if (rule === "mixed_lockdown") return Math.random() < 0.5 ? "laser" : "mine";
    const allowed = this.currentLevel?.allowedHazards;
    if (allowed?.length) {
      if (allowed.length === 1) return allowed[0];
      const heat = this.currentHeat();
      const unlockedByPressure = allowed.filter((type) => {
        if (type === "hunter") return alertLevel >= 2 || heat.tier >= 2;
        if (type === "jammer" || type === "mine") return alertLevel >= 2 || heat.tier >= 1;
        if (type === "laser") return alertLevel >= 1;
        return true;
      });
      const pool = unlockedByPressure.length ? unlockedByPressure : [allowed[0]];
      return pool[Math.floor(Math.random() * pool.length)];
    }
    const heat = this.currentHeat();
    const adaptiveMineBonus = Math.min(0.12, this.shieldUses * 0.03);
    if (heat.tier >= 4 && Math.random() < 0.22) return "hunter";
    if (heat.tier >= 3 && Math.random() < 0.16) return "jammer";
    if (heat.tier >= 2 && Math.random() < 0.24) return "hunter";
    if (heat.tier >= 1 && Math.random() < 0.34) return "tracker";
    if (alertLevel >= 5 && Math.random() < 0.28 + adaptiveMineBonus) return "mine";
    if (alertLevel >= 3 && Math.random() < 0.22) return "laser";
    return "drone";
  }

  private applySpecialDiamond(diamond: Diamond, point: Point): void {
    const alert = this.currentAlert();
    switch (diamond.type) {
      case "CLEAN_DIAMOND": {
        this.showDiscoveryCard("clean_diamond");
        const before = this.hazards.length;
        const minimum = alert.level >= 2 ? 1 : 0;
        this.hazards = this.specialDiamondSystem.removeNearestHazard(this.hazards, point, minimum);
        if (this.hazards.length < before) {
          this.hazardsDestroyed += 1;
          this.nextHazardSpawnIn = Math.max(this.nextHazardSpawnIn, alert.hazardSpawnInterval);
          this.showSpecialPopup(point, "Hazard Removed", "#f4fbff");
        } else {
          this.showSpecialPopup(point, "Security Holds", "#f4fbff");
        }
        break;
      }
      case "FREEZE_DIAMOND":
        this.showDiscoveryCard("freeze_diamond");
        if (this.freezeCooldown <= 0) {
          const freezeResistance = Math.min(0.6, this.freezeUses * 0.15);
          this.hazardsFrozenFor = 3 - freezeResistance;
          this.freezeCooldown = 15;
          this.freezeUses += 1;
          this.showSpecialPopup(point, "Hazards Frozen", "#8feaff");
        } else {
          this.showSpecialPopup(point, "Freeze Recharging", "#8feaff");
        }
        break;
      case "SHIELD_DIAMOND":
        this.showDiscoveryCard("shield_diamond");
        this.shieldCharges = this.specialDiamondSystem.grantShield(this.shieldCharges);
        this.showSpecialPopup(point, "Shield Ready", "#b8a6ff");
        break;
      case "TIME_DIAMOND":
        this.showDiscoveryCard("time_diamond");
        this.showSpecialPopup(point, "+5 Sec", "#ffdc72");
        break;
    }
  }

  private showSpecialPopup(position: Point, text: string, color: string): void {
    this.floatingTexts.push({
      position: { x: position.x, y: position.y - 30 },
      text,
      color,
      life: 1.2,
      maxLife: 1.2,
    });
  }

  private triggerShake(amount: number): void {
    if (this.save.settings.reducedMotion) return;
    this.shakeAmount = Math.max(this.shakeAmount, amount);
  }

  private vibrate(pattern: number | number[]): void {
    if (this.save.settings.reducedMotion) return;
    if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return;
    navigator.vibrate(pattern);
  }

  private hazardSpawnPoint(): Point {
    let point = this.randomPoint(90);
    for (let attempt = 0; attempt < 12; attempt += 1) {
      point = this.randomPoint(90);
      if (Math.hypot(point.x - this.player.position.x, point.y - this.player.position.y) >= 150) break;
    }
    return point;
  }

  private randomPoint(margin: number): Point {
    return {
      x: margin + Math.random() * Math.max(1, this.renderer.width - margin * 2),
      y: margin + 45 + Math.random() * Math.max(1, this.renderer.height - margin * 2 - 45),
    };
  }

  private spawnCollectionEffect(position: Point, color: string, score: number, time: number): void {
    const rewardText = time > 0 ? `+${score}  +${time.toFixed(1)}s` : `+${score}`;
    if (this.save.settings.reducedMotion) {
      this.floatingTexts.push({
        position: { x: position.x, y: position.y - 10 },
        text: rewardText,
        color,
        life: 0.5,
        maxLife: 0.5,
      });
      return;
    }
    for (let index = 0; index < 14; index += 1) {
      const angle = (index / 14) * Math.PI * 2 + Math.random() * 0.25;
      const speed = 75 + Math.random() * 115;
      const life = 0.55 + Math.random() * 0.25;
      this.particles.push({
        position: { ...position },
        velocity: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
        life,
        maxLife: life,
        color,
        size: 2.5 + Math.random() * 3.5,
      });
    }
    this.floatingTexts.push({
      position: { x: position.x, y: position.y - 10 },
      text: rewardText,
      color,
      life: 0.85,
      maxLife: 0.85,
    });
  }

  private updateEffects(delta: number): void {
    for (const particle of this.particles) {
      particle.life -= delta;
      particle.position.x += particle.velocity.x * delta;
      particle.position.y += particle.velocity.y * delta;
      particle.velocity.x *= 0.94;
      particle.velocity.y = particle.velocity.y * 0.94 + 55 * delta;
    }
    for (const label of this.floatingTexts) {
      label.life -= delta;
      label.position.y -= 34 * delta;
    }
    this.particles = this.particles.filter((particle) => particle.life > 0);
    this.floatingTexts = this.floatingTexts.filter((label) => label.life > 0);
  }

  private bumpCombo(): void {
    this.comboHud.classList.remove("bump");
    void this.comboHud.offsetWidth;
    this.comboHud.classList.add("bump");
  }

  private updateKeyboardMovement(delta: number): void {
    const before = { ...this.player.position };
    this.movement.move(this.player, this.input.direction(), delta, this.arenaBounds());
    if (Math.hypot(this.player.position.x - before.x, this.player.position.y - before.y) > 1) {
      this.tutorialMovementDetected = true;
    }
  }

  private arenaBounds(): ArenaBounds {
    return {
      left: this.player.radius,
      right: Math.max(this.player.radius, this.renderer.width - this.player.radius),
      top: 70,
      bottom: Math.max(70, this.renderer.height - this.player.radius),
    };
  }

  private diamondBounds(): ArenaBounds {
    const endless = this.mode === "endless" ? endlessDifficulty(this.elapsed) : undefined;
    const margin = this.currentLevel?.spawnMargin ?? endless?.spawnMargin ?? 65;
    return {
      left: margin,
      right: Math.max(margin, this.renderer.width - margin),
      top: this.hudSafeTop,
      bottom: Math.max(this.hudSafeTop, this.renderer.height - margin),
    };
  }

  private hazardBounds(): ArenaBounds {
    return {
      left: 25,
      right: Math.max(25, this.renderer.width - 25),
      top: this.hudSafeTop,
      bottom: Math.max(this.hudSafeTop, this.renderer.height - 35),
    };
  }

  // The HUD (score/level/time, objective banner, threat/heat meters, status
  // chips) is an absolutely-positioned overlay on top of the canvas, not
  // reserved header space - so diamonds/hazards need to know how far down
  // it actually extends on this viewport, or they spawn hidden behind it.
  // Measured from the real rendered panels (rather than a single hardcoded
  // constant) so it stays correct across breakpoints/orientations without
  // needing to be kept in sync with style.css by hand.
  private recomputeHudSafeTop(): void {
    const canvasRect = this.renderer.canvas.getBoundingClientRect();
    const panels = [this.objectiveHud, this.difficultyHud, this.alertHud, this.statusHud, this.heatHud]
      .filter((el) => el && !el.hidden && el.getBoundingClientRect().height > 0);
    // On wide viewports the HUD renders as a side column next to the canvas
    // rather than stacked on top of it, so only count a panel if it actually
    // overlaps the canvas horizontally - otherwise it isn't hiding anything.
    const overlapping = panels.filter((el) => {
      const rect = el.getBoundingClientRect();
      return rect.left < canvasRect.right && rect.right > canvasRect.left;
    });
    if (!overlapping.length) {
      this.hudSafeTop = 110;
      return;
    }
    const lowestPanelBottom = Math.max(...overlapping.map((el) => el.getBoundingClientRect().bottom));
    this.hudSafeTop = Math.max(110, Math.round(lowestPanelBottom - canvasRect.top) + 14);
  }

  private toggleSound(): void {
    this.save.settings.soundEnabled = !this.save.settings.soundEnabled;
    this.repository.save(this.save);
    this.applySettings();
    this.track("settings_changed", {
      setting: "sound",
      value: this.save.settings.soundEnabled,
    });
    if (this.save.settings.soundEnabled) this.audio.play("collect");
  }

  private toggleMotion(): void {
    this.save.settings.reducedMotion = !this.save.settings.reducedMotion;
    this.repository.save(this.save);
    this.applySettings();
    this.track("settings_changed", {
      setting: "reduced_motion",
      value: this.save.settings.reducedMotion,
    });
  }

  private applySettings(): void {
    this.audio.setEnabled(this.save.settings.soundEnabled);
    this.frame.classList.toggle("reduced-motion", this.save.settings.reducedMotion);
    this.require("sound-toggle").textContent = `Sound: ${this.save.settings.soundEnabled ? "on" : "off"}`;
    this.require("sound-toggle").setAttribute("aria-pressed", String(this.save.settings.soundEnabled));
    this.require("motion-toggle").textContent = `Motion: ${this.save.settings.reducedMotion ? "reduced" : "full"}`;
    this.require("motion-toggle").setAttribute("aria-pressed", String(this.save.settings.reducedMotion));
    this.require("settings-sound-toggle").textContent =
      `Sound: ${this.save.settings.soundEnabled ? "on" : "off"}`;
    this.require("settings-sound-toggle").setAttribute(
      "aria-pressed",
      String(this.save.settings.soundEnabled),
    );
    this.require("settings-motion-toggle").textContent =
      `Motion: ${this.save.settings.reducedMotion ? "reduced" : "full"}`;
    this.require("settings-motion-toggle").setAttribute(
      "aria-pressed",
      String(this.save.settings.reducedMotion),
    );
  }

  private refreshProfile(): void {
    const avatar = this.avatarDefinitions()[this.save.profile.avatar];
    this.require("best-value").textContent = this.save.highScore.toLocaleString();
    this.require("gold-value").textContent = this.save.totalGold.toLocaleString();
    this.require("heat-record-value").textContent = String(Math.round(this.save.statistics.highestHeat));
    const totalStars = Object.values(this.save.campaignStars).reduce((sum, stars) => sum + stars, 0);
    const currentChapter = Math.min(6, Math.floor((this.save.highestUnlockedLevel - 1) / 5) + 1);
    this.require("stars-value").textContent = `${totalStars}/90`;
    this.require("chapter-value").textContent = `${currentChapter}/6`;
    this.require("home-player-name").textContent = this.save.profile.name || "Vault Runner";
    this.require("home-player-title").textContent = `${avatar.label} - Rank ${this.save.playerRank}`;
    this.require("home-avatar").textContent = avatar.symbol;
    this.require("profile-summary").textContent = `${this.save.relics.length}/6 collected`;
    this.require("continue-level").textContent = `Level ${this.save.highestUnlockedLevel}`;
    const currentThreshold = RANK_THRESHOLDS[this.save.playerRank - 1] ?? 0;
    const nextThreshold = RANK_THRESHOLDS[this.save.playerRank] ?? currentThreshold + 1000;
    const rankRatio = Math.min(
      1,
      Math.max(0, (this.save.playerXp - currentThreshold) / (nextThreshold - currentThreshold)),
    );
    this.require("rank-progress-fill").style.transform = `scaleX(${rankRatio})`;
    const endless = this.require<HTMLButtonElement>("endless-button");
    endless.disabled = !this.save.endlessUnlocked;
    endless.textContent = this.save.endlessUnlocked ? "Endless Rush" : "Endless - Clear Level 10";
  }

  private refreshVaults(): void {
    const bank = this.require<HTMLButtonElement>("bank-vault");
    const bankState = this.require("bank-state");
    const unlocked = this.save.unlockedVaults.includes("bank");
    bank.disabled = !unlocked;
    bank.classList.toggle("unlocked", unlocked);
    bankState.textContent = unlocked ? "Unlocked" : "Locked";
    this.require("bank-copy").textContent = unlocked
      ? "14 seconds / heavy security / higher pressure"
      : `Reach Rank 3 or complete ${Math.max(0, 15 - this.save.totalRuns)} more runs`;
  }

  private require<T extends HTMLElement = HTMLElement>(id: string): T {
    const element = this.root.querySelector<T>(`#${id}`);
    if (!element) throw new Error(`Missing #${id}`);
    return element;
  }

  private initialScreen(): GameState {
    if (!this.save.profile.name) return "ONBOARDING";
    return this.postProfileScreen();
  }

  private postProfileScreen(): GameState {
    return shouldShowOnboarding(this.save) ? "ONBOARDING_ENTRY" : "MAIN_MENU";
  }

  private maybeShowWhatsNew(): void {
    if (shouldShowWhatsNew(this.save)) this.whatsNewCard.hidden = false;
  }

  private renderOnboardingEntry(): void {
    const copy = controlCopyFor(this.platformContext());
    this.require("tutorial-control-preview").innerHTML = `
      <strong>${copy.title}</strong>
      <p>${copy.body}</p>
      <div class="tutorial-hints">${copy.hints.map((hint) => `<span>${hint}</span>`).join("")}</div>
    `;
  }

  private renderManual(): void {
    const copy = controlCopyFor(this.platformContext());
    const sections = [
      ["Controls", `${copy.hints.join(" - ")}. Keyboard and mouse remain available on desktop.`],
      ["Diamonds", "Common diamonds give score and time. Rare gems are worth more and can extend risky routes."],
      ["Special Diamonds", "Shield blocks a hit, Clean removes a hazard, Freeze pauses security, Time adds time."],
      ["Security Heat", "Strong play raises Heat. Higher Heat means more danger, better score, and stronger rewards."],
      ["Hazards", "Lasers, drones, mines, jammers, and hunters are telegraphed before they become dangerous."],
      ["Relics", "Master Vault relics are small permanent trophies with helpful bonuses."],
      ["Campaign Stars", "Finish with more time remaining to earn up to three stars."],
      ["Endless Rush", "Unlocked after Level 10. Survive as long as possible with infinite scaling."],
    ];
    this.require("manual-grid").innerHTML = sections.map(([title, body]) => `
      <article class="manual-card"><h3>${title}</h3><p>${body}</p></article>
    `).join("");
  }

  private platformContext(): PlatformContext {
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches;
    const touch = navigator.maxTouchPoints > 0 || window.innerWidth <= 760;
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes("android") && touch) return "android";
    if (/iphone|ipad|ipod/.test(userAgent) && touch) return "ios";
    if (standalone && touch) return "pwa";
    return touch ? "mobile" : "desktop";
  }

  private showDiscoveryCard(featureId: string, title?: string, body?: string): void {
    const copy = FEATURE_DISCOVERY[featureId] ?? { title: title ?? "New Intel", body: body ?? "" };
    if (featureAlreadySeen(this.save, featureId)) return;
    this.save = this.repository.discoverFeature(this.save, featureId);
    this.require("discovery-title").textContent = copy.title;
    this.require("discovery-copy").textContent = copy.body;
    this.discoveryCard.hidden = false;
  }

  private maybeShowChapterIntro(level: CampaignLevel): void {
    const intro = CHAPTER_INTROS[Math.floor((level.id - 1) / 5)];
    if (!intro || featureAlreadySeen(this.save, intro.id)) return;
    // Campaign details are already visible on the map and HUD. Mark the chapter read
    // without blocking the opening seconds of a playable run with a full-screen card.
    this.save = this.repository.discoverFeature(this.save, intro.id);
  }

  private adPlacement(placement: "menu" | "campaign"): string {
    const slot = this.ads.slotFor(placement);
    return `
      <aside class="ad-placement" data-ad-placement="${placement}" aria-label="Advertisement">
        <strong>AdSense test placement</strong>
        <span>${placement === "menu" ? "desktop-left-side-rail" : "responsive-banner"}</span>
        <small>Shown locally after ads consent. Real ads load only on production with AdSense IDs.</small>
        <ins class="adsbygoogle"
          style="display:block"
          data-ad-client="${import.meta.env.VITE_ADSENSE_CLIENT ?? ""}"
          data-ad-slot="${slot ?? ""}"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins>
      </aside>`;
  }

  private startCampaignLevel(id: number): void {
    if (id > this.save.highestUnlockedLevel) return;
    const chapter = Math.floor((id - 1) / 5) + 1;
    if (chapter !== this.activeChapter) {
      this.activeChapter = chapter;
    }
    this.mode = "campaign";
    this.currentLevel = campaignLevel(id);
    this.track("game_opened", {
      mode: this.mode,
      level_id: this.currentLevel.id,
      vault: this.currentLevel.vault,
    });
    this.maybeShowChapterIntro(this.currentLevel);
    this.startRun(this.currentLevel.vault);
  }

  private startEndless(vault: VaultId): void {
    this.mode = "endless";
    this.currentLevel = undefined;
    this.track("game_opened", {
      mode: this.mode,
      vault,
    });
    this.startRun(vault);
  }

  private nextLevelFromResults(): void {
    if (!this.currentLevel || !this.levelSucceeded || this.currentLevel.id >= 30) return;
    this.startCampaignLevel(this.currentLevel.id + 1);
  }

  private replayCurrentRun(): void {
    if (this.currentLevel) {
      this.startCampaignLevel(this.currentLevel.id);
      return;
    }
    this.startEndless(this.vault);
  }

  private async shareResult(): Promise<void> {
    const runResult = this.lastResult;
    if (!runResult) return;
    const shareUrl = "https://vibwebs.com/games/diamond-rush";
    const text = runResult.level
      ? `I scored ${runResult.score.toLocaleString()} points on Level ${runResult.level} in Diamond Dash!`
      : `I scored ${runResult.score.toLocaleString()} points in Diamond Dash!`;
    const button = this.require<HTMLButtonElement>("share-result-button");
    const originalLabel = button.textContent;
    const flashLabel = (label: string): void => {
      button.textContent = label;
      window.setTimeout(() => {
        button.textContent = originalLabel;
      }, 1600);
    };
    const trackShared = (method: "share_sheet" | "clipboard"): void => {
      this.track("score_shared", { method, level_id: runResult.level, score: runResult.score });
    };

    const payload = { title: "Diamond Dash", text, url: shareUrl };
    const result = await window.GameShare.share(payload);
    if (result === "shared") {
      trackShared("share_sheet");
      return;
    }
    if (result === "cancelled") {
      // Diamond Dash's existing product policy (predates this integration):
      // a cancelled native share sheet still falls through to a clipboard
      // copy. GameShare.share() stops at "cancelled" for callers that want
      // that distinction (RuneVault); copy() explicitly requests the
      // clipboard fallback anyway - same pattern used for Treasure Grid.
      const copyResult = await window.GameShare.copy(payload);
      if (copyResult === "copied") {
        flashLabel("Copied!");
        trackShared("clipboard");
        return;
      }
      flashLabel("Copy failed");
      return;
    }
    if (result === "copied") {
      flashLabel("Copied!");
      trackShared("clipboard");
      return;
    }
    flashLabel("Copy failed");
  }

  private shareToSocial(platform: "facebook" | "x"): void {
    const runResult = this.lastResult;
    if (!runResult) return;
    const shareUrl = "https://vibwebs.com/games/diamond-rush";
    const text = runResult.level
      ? `I scored ${runResult.score.toLocaleString()} points on Level ${runResult.level} in Diamond Dash!`
      : `I scored ${runResult.score.toLocaleString()} points in Diamond Dash!`;
    const urls = window.GameShare.socialUrls({ title: "Diamond Dash", text, url: shareUrl });
    window.open(urls[platform], "_blank", "noopener,noreferrer");
  }

  renderUpgradeChoices(result: RunResult): void {
    const replay = this.require<HTMLButtonElement>("replay-button");
    const next = this.require<HTMLButtonElement>("next-level-button");
    const shouldChoose = Boolean(
      this.mode === "campaign"
      && this.currentLevel
      && result.success
      && !this.currentLevel.master
      && this.currentLevel.id < 30,
    );
    this.upgradePanel.hidden = !shouldChoose;
    replay.hidden = shouldChoose;
    if (shouldChoose) next.hidden = true;
    if (!shouldChoose || !this.currentLevel) return;
    return;
    this.require("upgrade-grid").innerHTML = upgradeChoicesFor(this.currentLevel!.id)
      .map((upgrade) => {
        const presentation = this.upgradePresentation(upgrade.id, upgrade.category);
        return `
        <button class="upgrade-card" type="button" data-upgrade="${upgrade.id}">
          <strong>${presentation.icon} ${upgrade.name}</strong>
          <em>${upgrade.category}</em>
          <small>${presentation.help}</small>
          <span>${upgrade.description}</span>
          <b>Choose</b>
        </button>`;
      })
      .join("");
  }

  upgradePresentation(id: ChapterUpgradeId, category: string): { icon: string; help: string } {
    const copy: Record<ChapterUpgradeId, { icon: string; help: string }> = {
      combo_window: { icon: "CB", help: "Keep combos alive longer." },
      time_bonus: { icon: "TM", help: "Gain more time from diamonds." },
      pickup_radius: { icon: "MG", help: "Collect diamonds from farther away." },
      slow_security: { icon: "SL", help: "Security hazards move slower." },
      starting_shield: { icon: "SH", help: "Start the next run protected." },
    };
    return copy[id] ?? {
      icon: category.toLowerCase() === "survival" ? "SV" : category.toLowerCase() === "score" ? "SC" : "UP",
      help: "Improves this chapter run.",
    };
  }

  chooseUpgrade(id: ChapterUpgradeId): void {
    if (this.upgradePanel.hidden) return;
    this.chapterUpgrades = applyChapterUpgrade(this.chapterUpgrades, id);
    this.upgradePanel.hidden = true;
    this.require<HTMLButtonElement>("replay-button").hidden = false;
    this.nextLevelFromResults();
  }

  private avatarOptions(selected: PlayerAvatar, group = "avatar"): string {
    return (Object.entries(this.avatarDefinitions()) as Array<
      [PlayerAvatar, { symbol: string; label: string }]
    >).map(([id, avatar]) => `
      <label class="avatar-option">
        <input type="radio" name="${group}" value="${id}" ${id === selected ? "checked" : ""}>
        <span>${avatar.symbol}</span><small>${avatar.label}</small>
      </label>`).join("");
  }

  private avatarDefinitions(): Record<PlayerAvatar, { symbol: string; label: string }> {
    return {
      cracker: { symbol: "VC", label: "Vault Cracker" },
      hacker: { symbol: "HK", label: "Hacker" },
      ghost: { symbol: "GH", label: "Ghost" },
      king: { symbol: "DK", label: "Diamond King" },
      breaker: { symbol: "SB", label: "Security Breaker" },
    };
  }

  private selectedAvatar(containerId: string): PlayerAvatar {
    const selected = this.require(containerId)
      .querySelector<HTMLInputElement>("input[type='radio']:checked")?.value;
    return (selected as PlayerAvatar | undefined) ?? "cracker";
  }

  private renderProfile(): void {
    const avatar = this.avatarDefinitions()[this.save.profile.avatar];
    this.require("profile-avatar-large").textContent = avatar.symbol;
    this.require("relic-count").textContent = `${this.save.relics.length} of 6 relics collected`;
    this.require<HTMLInputElement>("profile-name-input").value = this.save.profile.name;
    this.profileScreen.querySelectorAll<HTMLInputElement>("input[name='profile-avatar']")
      .forEach((input) => { input.checked = input.value === this.save.profile.avatar; });
    this.require("relic-collection").innerHTML = Object.values(RELICS).map((relic) => {
      const unlocked = this.save.relics.includes(relic.id);
      return `<article class="relic-card${unlocked ? " unlocked" : ""}">
        <span>${unlocked ? "ON" : "LOCK"}</span><div><strong>${relic.name}</strong>
        <small>${unlocked ? relic.description : "Defeat the matching Master Vault"}</small></div>
      </article>`;
    }).join("");
  }

  private renderStatistics(): void {
    const stats = this.save.statistics;
    const entries = [
      ["Total diamonds", this.save.totalDiamondsCollected.toLocaleString()],
      ["Best combo", `x${stats.bestCombo}`],
      ["Longest survival", `${stats.longestSurvivalSeconds.toFixed(1)}s`],
      ["Highest Heat", Math.round(stats.highestHeat)],
      ["Master Vault wins", stats.masterVaultWins],
      ["Rare gems", stats.rareGemsCollected],
      ["Hazards avoided", stats.hazardsAvoided],
      ["Hazards removed", stats.hazardsDestroyed],
      ["Special diamonds", stats.specialDiamondsCollected],
      ["Endless best", stats.endlessBestScore.toLocaleString()],
      ["Campaign stars", Object.values(this.save.campaignStars).reduce((sum, stars) => sum + stars, 0)],
      ["Total runs", this.save.totalRuns],
    ];
    this.require("statistics-grid").innerHTML = entries.map(([label, value]) =>
      `<article class="stat-card"><span>${label}</span><strong>${value}</strong></article>`).join("");
  }

  private renderCampaignMap(): void {
    const grid = this.require("campaign-map-grid");
    grid.innerHTML = Array.from({ length: 6 }, (_, chapterIndex) => {
      const levels = CAMPAIGN_LEVELS.slice(chapterIndex * 5, chapterIndex * 5 + 5);
      const chapterStars = levels.reduce(
        (sum, level) => sum + (this.save.campaignStars[String(level.id)] ?? 0),
        0,
      );
      const chapterDifficulty = ["Easy", "Easy", "Medium", "Hard", "Expert", "Master"][chapterIndex];
      return `<section class="chapter-path vault-${levels[0].vault}">
        <header><div><span>Chapter ${chapterIndex + 1}</span><h3>${levels[0].vaultName}</h3></div><strong>${chapterStars}/15 stars</strong></header>
        <div class="chapter-difficulty difficulty-${chapterDifficulty.toLowerCase()}">${chapterDifficulty}</div>
        <div class="chapter-levels">${levels.map((level) => {
          const locked = level.id > this.save.highestUnlockedLevel;
          const stars = this.save.campaignStars[String(level.id)] ?? 0;
          const current = level.id === this.save.highestUnlockedLevel;
          const completed = stars > 0;
          return `<button class="level-node vault-${level.vault}${level.master ? " master" : ""}${current ? " current" : ""}${completed ? " completed" : ""} difficulty-${level.difficulty.toLowerCase()}"
            type="button" data-level="${level.id}" ${locked ? "disabled" : ""}>
            <span>${level.master ? "MASTER" : level.id}</span>
            <strong>${locked ? "Locked" : difficultyTagFor(level)}</strong>
            <small class="level-node-objective">${locked ? "-" : `Target: ${masterRuleName(level.masterRule) || objectiveName(level.objective)}`}</small>
            <small>${locked
              ? ""
              : [
                rewardUnlockFor(level),
                `${"*".repeat(stars)}${"-".repeat(3 - stars)}`,
              ].filter(Boolean).join(" - ")}</small>
          </button>`;
        }).join("")}</div>
      </section>`;
    }).join("");
    grid.querySelectorAll<HTMLElement>("[data-level]").forEach((button) => {
      button.addEventListener("click", () => this.startCampaignLevel(Number(button.dataset.level)));
    });
  }
}
